import { useEffect, useId, useRef, useState } from 'react'
import { useCopy } from '@/i18n/useCopy'
import { useSessionStore } from '@/store/sessionStore'
import type { OnboardingView } from './runner'
import styles from './Welcome.module.css'

/**
 * The first conversation, on screen.
 *
 * There is no wizard here and there is no form. The orb above this is the
 * interface and the progress; everything below it is the small amount that
 * genuinely has to be readable rather than only audible, and it appears one
 * piece at a time:
 *
 *   the disclosure   during the consent question, and nowhere else
 *   the caption      whatever Namu has just said, always
 *   the keyboard     after three failures on one question, and not before
 *   the skip         whenever there is a question to skip, which is the one
 *                    control on screen and always the same one
 *
 * The order down the screen is fixed even though the pieces come and go. A
 * control that moves because something above it appeared is a control somebody
 * has to look for, and looking for the way out is the thing this whole design
 * is trying to stop.
 */

export interface WelcomeProps {
  view: OnboardingView
  onSkip(): void
  onTyped(text: string): void
}

export function Welcome({ view, onSkip, onTyped }: WelcomeProps) {
  const copy = useCopy()

  return (
    <div className={styles.welcome} data-testid="onboarding">
      {view.disclosure ? <Disclosure points={view.disclosure} /> : null}

      <SpokenLine omit={view.disclosure} />

      {/*
        Keyed by the step, so a keyboard offered again on a later question
        arrives empty and focused rather than holding the answer to the
        question before it.
      */}
      {view.typedOffered ? <TypedAnswer key={view.stepId} onSubmit={onTyped} /> : null}

      {view.asking ? (
        <button type="button" className={styles.skip} onClick={onSkip} data-testid="skip-question">
          {copy.ui.control.skip}
        </button>
      ) : null}
    </div>
  )
}

/**
 * The consent disclosure, shown while it is spoken.
 *
 * Both, and the reasoning survives being restated because it is exactly the
 * kind of thing that gets simplified away by somebody who has not thought
 * about it: a spoken yes is a valid signature but a spoken disclosure is not a
 * valid record, and a written disclosure nobody reads aloud is no disclosure at
 * all to somebody using this by voice or with a screen reader.
 *
 * Static rather than live. A screen reader hears these words once as they are
 * spoken, through the transcript region the whole app shares; this is the copy
 * they can navigate back to and read again, which is what a record is for. A
 * live region here would announce the same four sentences a second time.
 */
function Disclosure({ points }: { points: readonly string[] }) {
  const copy = useCopy()
  const titleId = useId()

  return (
    <section
      className={styles.disclosure}
      aria-labelledby={titleId}
      data-testid="consent-disclosure"
    >
      <h2 className={styles.disclosureTitle} id={titleId}>
        {copy.ui.onboarding.disclosureTitle}
      </h2>
      <ul className={styles.points} role="list">
        {points.map((point) => (
          <li className={styles.point} key={point}>
            {/* The Sahel dot from the mark, reused as the list marker. */}
            <span className={styles.dot} aria-hidden="true" />
            <span>{point}</span>
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * What Namu has just said, for anyone who cannot hear it.
 *
 * Captioned in onboarding regardless of the captions setting, which is off by
 * default everywhere else. This is the one conversation where nobody has
 * learned yet what the orb means, it is the conversation that contains the
 * terms, and it is the conversation a person is most likely to be having in a
 * market at midday. None of those wait for somebody to find a setting.
 *
 * `aria-hidden`, like the shared captions band, because the same words reach a
 * screen reader through the single transcript region in `LiveRegions`. Two
 * copies of one sentence is not twice the accessibility.
 */
function SpokenLine({ omit }: { omit: readonly string[] | null }) {
  const turns = useSessionStore((state) => state.turns)

  const spoken = turns.filter((turn) => turn.speaker === 'namu').at(-1)
  if (!spoken) return null

  const text = withoutDisclosure(spoken.text, omit)
  if (text === '') return null

  return (
    <p className={styles.caption} aria-hidden="true" data-testid="onboarding-caption">
      {text}
    </p>
  )
}

/**
 * Drops the four consent points from the caption of the utterance that
 * contains them.
 *
 * The consent step speaks the points and the question as one line, because
 * that is one breath rather than five. Captioning it whole puts the terms on
 * screen twice, once as the list above and once as a paragraph, and a person
 * then has to read both to satisfy themselves they say the same thing. The
 * prefix is compared exactly because the step builds it by joining the same
 * array this is handed, so there is nothing to be clever about.
 */
function withoutDisclosure(text: string, omit: readonly string[] | null): string {
  if (!omit || omit.length === 0) return text
  const spokenPoints = omit.join(' ')
  return text.startsWith(spokenPoints) ? text.slice(spokenPoints.length).trim() : text
}

/**
 * The way out of a loud market.
 *
 * Offered after three failures on one question and never before, because
 * putting a text field on screen in a product whose premise is that you do not
 * need one teaches people to reach for it first. A real label above a real
 * field, not a placeholder: a placeholder disappears the moment somebody starts
 * typing, which is exactly when they most need to know what was asked, and no
 * screen reader treats one as the field's name.
 */
function TypedAnswer({ onSubmit }: { onSubmit(text: string): void }) {
  const copy = useCopy()
  const fieldId = useId()
  const [value, setValue] = useState('')
  const fieldRef = useRef<HTMLInputElement | null>(null)

  /*
   * Focused on arrival. Moving somebody's focus uninvited is normally wrong,
   * and here it is not: this panel appears only in direct answer to Namu
   * saying "you can type it instead", after three attempts at being heard, and
   * a keyboard user who then has to hunt for the field has been failed twice.
   */
  useEffect(() => {
    fieldRef.current?.focus()
  }, [])

  return (
    <form
      className={styles.typed}
      onSubmit={(event) => {
        event.preventDefault()
        const answer = value.trim()
        if (answer === '') return
        setValue('')
        onSubmit(answer)
      }}
    >
      <label className={styles.typedLabel} htmlFor={fieldId}>
        {copy.ui.onboarding.typedLabel}
      </label>
      <div className={styles.typedRow}>
        <input
          id={fieldId}
          ref={fieldRef}
          className={styles.typedField}
          type="text"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          /* A name and a place are not words a dictionary knows, and every one
             of these corrects them into something else on a phone keyboard. */
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="send"
        />
        <button type="submit" className={styles.typedSubmit}>
          {copy.ui.onboarding.typedSubmit}
        </button>
      </div>
    </form>
  )
}
